import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, AlertTriangle, Receipt, Banknote, ArrowRightLeft, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';

/**
 * DeleteTransactionDialog - Confirmation dialog with impact warnings for deleting transactions
 * 
 * Props:
 * - open: boolean - Controls visibility
 * - onOpenChange: (open: boolean) => void - Callback for open state changes
 * - transaction: object - The transaction to delete (with linked data)
 * - onConfirm: () => Promise<void> - Callback when deletion is confirmed
 * - isLoading: boolean - Whether the deletion is in progress
 */
const DeleteTransactionDialog = ({
  open,
  onOpenChange,
  transaction,
  onConfirm,
  isLoading = false,
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  if (!transaction) return null;

  // Determine transaction type
  const hasInvoicePayment = !!transaction.linked_invoice_payment;
  const hasPurchasePayment = !!transaction.linked_purchase_payment;
  const hasTransfer = !!transaction.linked_transfer;
  const isManual = !hasInvoicePayment && !hasPurchasePayment && !hasTransfer;

  // Calculate the new status for linked entities
  const getNewInvoiceStatus = () => {
    if (!hasInvoicePayment) return null;
    const invoice = transaction.linked_invoice_payment.invoice;
    const paymentAmount = parseFloat(transaction.amount) || 0;
    const currentAmountPaid = parseFloat(invoice.amount_paid) || 0;
    const invoiceTotal = parseFloat(invoice.total) || 0;
    const newAmountPaid = currentAmountPaid - paymentAmount;
    
    if (newAmountPaid <= 0) {
      return 'Sent';
    } else if (newAmountPaid >= invoiceTotal) {
      return 'Paid in Full';
    } else {
      return 'Part Paid';
    }
  };

  const getNewPurchaseStatus = () => {
    if (!hasPurchasePayment) return null;
    const purchase = transaction.linked_purchase_payment.purchase;
    const paymentAmount = parseFloat(transaction.amount) || 0;
    const currentAmountPaid = parseFloat(purchase.amount_paid) || 0;
    const purchaseTotal = parseFloat(purchase.total) || 0;
    const newAmountPaid = currentAmountPaid - paymentAmount;
    
    if (newAmountPaid <= 0) {
      return 'Unpaid';
    } else if (newAmountPaid >= purchaseTotal) {
      return 'Paid';
    } else {
      return 'Part Paid';
    }
  };

  const getNewBalanceDue = () => {
    if (hasInvoicePayment) {
      const invoice = transaction.linked_invoice_payment.invoice;
      const currentBalanceDue = parseFloat(invoice.balance_due) || 0;
      const paymentAmount = parseFloat(transaction.amount) || 0;
      return currentBalanceDue + paymentAmount;
    }
    if (hasPurchasePayment) {
      const purchase = transaction.linked_purchase_payment.purchase;
      const currentBalanceDue = parseFloat(purchase.balance_due) || 0;
      const paymentAmount = parseFloat(transaction.amount) || 0;
      return currentBalanceDue + paymentAmount;
    }
    return null;
  };

  // Get the action label and title
  const getTitle = () => {
    if (hasInvoicePayment) return 'Remove Invoice Payment';
    if (hasPurchasePayment) return 'Remove Purchase Payment';
    if (hasTransfer) return 'Delete Transfer';
    return 'Delete Transaction';
  };

  const getActionLabel = () => {
    if (hasInvoicePayment || hasPurchasePayment) return 'Remove Payment';
    if (hasTransfer) return 'Delete Transfer';
    return 'Delete Transaction';
  };

  const getDescription = () => {
    if (hasInvoicePayment) {
      const invoiceNum = transaction.linked_invoice_payment.invoice.invoice_num;
      return `Are you sure you want to remove this payment from Invoice #${invoiceNum}?`;
    }
    if (hasPurchasePayment) {
      const purchaseNum = transaction.linked_purchase_payment.purchase.purchase_num;
      return `Are you sure you want to remove this payment from Purchase #${purchaseNum}?`;
    }
    if (hasTransfer) {
      return 'Are you sure you want to delete this transfer?';
    }
    return 'Are you sure you want to delete this transaction?';
  };

  // Build impact list
  const getImpactItems = () => {
    const items = [];

    if (hasInvoicePayment) {
      const invoice = transaction.linked_invoice_payment.invoice;
      const newStatus = getNewInvoiceStatus();
      const newBalance = getNewBalanceDue();

      items.push({
        icon: Receipt,
        text: `Remove the ${formatCurrency(transaction.amount)} payment from Invoice #${invoice.invoice_num}`,
        color: 'text-red-600 dark:text-red-400',
      });

      if (newStatus && newStatus !== invoice.status) {
        items.push({
          icon: AlertTriangle,
          text: `Change invoice status from "${invoice.status}" to "${newStatus}"`,
          color: 'text-amber-600 dark:text-amber-400',
        });
      }

      if (newBalance !== null) {
        items.push({
          icon: Banknote,
          text: `Update balance due to ${formatCurrency(newBalance)}`,
          color: 'text-amber-600 dark:text-amber-400',
        });
      }
    }

    if (hasPurchasePayment) {
      const purchase = transaction.linked_purchase_payment.purchase;
      const newStatus = getNewPurchaseStatus();
      const newBalance = getNewBalanceDue();

      items.push({
        icon: Receipt,
        text: `Remove the ${formatCurrency(transaction.amount)} payment from Purchase #${purchase.purchase_num}`,
        color: 'text-red-600 dark:text-red-400',
      });

      if (newStatus && newStatus !== purchase.status) {
        items.push({
          icon: AlertTriangle,
          text: `Change purchase status from "${purchase.status}" to "${newStatus}"`,
          color: 'text-amber-600 dark:text-amber-400',
        });
      }

      if (newBalance !== null) {
        items.push({
          icon: Banknote,
          text: `Update balance due to ${formatCurrency(newBalance)}`,
          color: 'text-amber-600 dark:text-amber-400',
        });
      }
    }

    if (hasTransfer) {
      const fromAccount = transaction.transaction_type === 'withdrawal' 
        ? transaction.bank_account.account_name 
        : transaction.linked_transfer.bank_account.account_name;
      const toAccount = transaction.transaction_type === 'deposit'
        ? transaction.bank_account.account_name
        : transaction.linked_transfer.bank_account.account_name;

      items.push({
        icon: ArrowRightLeft,
        text: `Remove the ${formatCurrency(transaction.amount)} transfer`,
        color: 'text-red-600 dark:text-red-400',
      });

      items.push({
        icon: Banknote,
        text: `Update "${fromAccount}" balance (credit back ${formatCurrency(transaction.amount)})`,
        color: 'text-amber-600 dark:text-amber-400',
      });

      items.push({
        icon: Banknote,
        text: `Update "${toAccount}" balance (debit ${formatCurrency(transaction.amount)})`,
        color: 'text-amber-600 dark:text-amber-400',
      });
    }

    // Always add bank account balance update for manual transactions
    if (isManual) {
      const accountName = transaction.bank_account?.account_name || 'Bank account';
      const direction = transaction.transaction_type === 'deposit' ? 'decrease' : 'increase';

      items.push({
        icon: Banknote,
        text: `Remove ${formatCurrency(transaction.amount)} ${transaction.transaction_type} from "${accountName}"`,
        color: 'text-red-600 dark:text-red-400',
      });

      items.push({
        icon: Banknote,
        text: `Account balance will ${direction} by ${formatCurrency(transaction.amount)}`,
        color: 'text-amber-600 dark:text-amber-400',
      });
    }

    // Bank account balance update (for linked transactions)
    if ((hasInvoicePayment || hasPurchasePayment) && transaction.bank_account) {
      items.push({
        icon: Banknote,
        text: `Update "${transaction.bank_account.account_name}" balance`,
        color: 'text-amber-600 dark:text-amber-400',
      });
    }

    return items;
  };

  const impactItems = getImpactItems();

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-md bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-popover)] rounded-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[var(--ds-border-divider)] flex items-center justify-between">
            <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-[var(--ds-text-primary)]">
              <AlertTriangle className="h-5 w-5 text-[var(--ds-status-danger)]" />
              {getTitle()}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="p-1.5 text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-hover-bg)] rounded-md transition-colors"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>
          
          {/* Content */}
          <div className="px-6 py-5 space-y-4">
            {/* Description */}
            <p className="text-[var(--ds-text-primary)]">
              {getDescription()}
            </p>

            {/* Impact Warning Box */}
            <div className="bg-[var(--ds-status-warning-bg)] border border-[var(--ds-status-warning)]/30 rounded-[var(--ds-radius-md)] p-4">
              <h4 className="text-sm font-medium text-[var(--ds-text-primary)] mb-3">
                This will:
              </h4>
              <ul className="space-y-2">
                {impactItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${item.color}`} />
                      <span className="text-[var(--ds-text-secondary)]">{item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Permanent Warning */}
            <p className="text-sm text-[var(--ds-status-danger)] font-medium">
              This action cannot be undone.
            </p>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[var(--ds-border-divider)] flex items-center justify-end gap-3 bg-[var(--ds-surface-panel2)]">
            <Button
              variant="secondary"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={isLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                getActionLabel()
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DeleteTransactionDialog;
